import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { WorkStep } from '../generic/types/ObjectFormTypes';
import logo from '../../assets/114.jpg';
import { formatNumber, formatCurrency, formatFinnishDate } from '../../utilities/PdfUtils';

// Define styles for the PDF
const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontSize: 10,
        fontFamily: 'Helvetica',
    },
    header: {
        marginBottom: 20,
    },
    logoRow: {
        flexDirection: 'row',
        marginBottom: 15,
    },
    logoCell: {
        width: '60%',
    },
    logo: {
        width: 150,
        height: 'auto',
    },
    emptyCell: {
        width: '40%',
    },
    spacer: {
        marginBottom: 10,
    },
    headerRowTwoCol: {
        flexDirection: 'row',
        marginBottom: 5,
        fontSize: 10,
    },
    headerCellLeft: {
        width: '60%',
    },
    headerCellRight: {
        width: '40%',
    },
    headerCellBold: {
        fontWeight: 'bold',
    },
    headerCellLabel: {
        fontWeight: 'bold',
    },
    headerRowFullWidth: {
        marginBottom: 5,
        fontSize: 10,
    },
    headerRowFullWidthLabel: {
        fontWeight: 'bold',
    },
    title: {
        fontSize: 14,
        fontWeight: 'bold',
        textAlign: 'right',
    },
    groupHeader: {
        fontSize: 12,
        fontWeight: 'bold',
        backgroundColor: '#e9ecef',
        padding: 8,
        marginTop: 15,
        marginBottom: 5,
        color: '#1c7ed6',
    },
    table: {
        marginTop: 10,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#dee2e6',
        borderBottomWidth: 2,
        borderBottomColor: '#495057',
        paddingVertical: 8,
        paddingHorizontal: 4,
        fontWeight: 'bold',
        fontSize: 8,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#dee2e6',
        paddingVertical: 6,
        paddingHorizontal: 4,
        fontSize: 8,
    },
    tableRowAlt: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#dee2e6',
        paddingVertical: 6,
        paddingHorizontal: 4,
        backgroundColor: '#f8f9fa',
        fontSize: 8,
    },
    col1: { width: '35%' }, // Type of Coating / Work Phase
    col2: { width: '10%', textAlign: 'center' }, // Unit
    col3: { width: '13%', textAlign: 'right' }, // Amount
    col4: { width: '13%', textAlign: 'right' }, // Surface Area
    col5: { width: '13%', textAlign: 'right' }, // Tons
    col6: { width: '16%', textAlign: 'right' }, // Price
    totalSection: {
        marginTop: 20,
        paddingTop: 10,
        borderTopWidth: 2,
        borderTopColor: '#495057',
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginBottom: 8,
        paddingRight: 4,
    },
    totalLabel: {
        fontWeight: 'bold',
        marginRight: 20,
        fontSize: 11,
    },
    totalValue: {
        fontWeight: 'bold',
        width: 100,
        textAlign: 'right',
        fontSize: 11,
    },
    descriptionSection: {
        marginTop: 20,
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: '#dee2e6',
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        marginBottom: 10,
        color: '#1976d2',
    },
    descriptionText: {
        fontSize: 10,
        lineHeight: 1.5,
        color: '#333',
    },
    imageSection: {
        marginTop: 15,
        marginBottom: 15,
    },
    imageTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        marginBottom: 8,
        color: '#1976d2',
    },
    image: {
        maxWidth: '100%',
        maxHeight: 300,
        objectFit: 'contain',
        marginBottom: 10,
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 30,
        fontSize: 8,
        color: '#000',
    },
});

interface PlannedWorkStepsPDFProps {
    plannedWorkSteps: WorkStep[];
    form: any;
    objectId?: number;
    contractId?: number;
    contract?: any;
    mapImageUrl?: string; // Optional map image
    objectImageUrl?: string; // Optional main object image
}

export const PlannedWorkStepsPDF = ({
    plannedWorkSteps,
    form,
    objectId,
    contract,
    mapImageUrl,
    objectImageUrl,
}: PlannedWorkStepsPDFProps) => {
    // Extract values from contract data (primary source) or form (fallback)
    const contractName = contract?.name || form?.values?.contractName || form?.values?.jobName || '';
    const contractCode = contract?.contractCode || form?.values?.contractCode || '';
    const location = form?.values?.location || form?.values?.name || '';
    const statusName = form?.values?.statusName || '';
    const typeName = form?.values?.typeName || '';
    const plannedYear = form?.values?.plannedYear 
        ? new Date(form?.values?.plannedYear).getFullYear().toString()
        : '';
    const description = form?.values?.description || '';
    const orderNumber = form?.values?.orderNumber || form?.values?.orderName || '';
    const networkNumber = form?.values?.networkNumber || form?.values?.networkName || '';
    
    // Legacy: getPageText labels
    const orgLabel1 = 'getPageText("TEXT_LABEL_NIMI1", true)';
    const orgLabel2 = 'getPageText("TEXT_LABEL_NIMI2", true)';
    const orgLabel3 = 'getPageText("TEXT_LABEL_NIMI3_" + strenght_id, true)';
    
    // Calculate total cost
    const totalCost = plannedWorkSteps
        .filter(step => !step.isGroupHeader)
        .reduce((sum, step) => sum + (step.priceC || 0), 0);


    // Group items by their group headers (similar to MeasurementReportPDF)
    const groupedItems: { [key: string]: WorkStep[] } = {};
    let currentGroup = '';

    plannedWorkSteps.forEach((step) => {
        if (step.isGroupHeader) {
            currentGroup = step.typeOfCoating;
            groupedItems[currentGroup] = [];
        } else if (currentGroup) {
            groupedItems[currentGroup].push(step);
        } else {
            // If no group header found yet, create a default group
            if (!groupedItems['Work Items']) {
                groupedItems['Work Items'] = [];
            }
            groupedItems['Work Items'].push(step);
        }
    });

    return (
        <Document>
            <Page size="A4" orientation="portrait" style={styles.page}>
                {/* Header Section */}
                <View style={styles.header}>
                    {/* Logo Row */}
                    <View style={styles.logoRow}>
                        <View style={styles.logoCell}>
                            <Image src={logo} style={styles.logo} />
                        </View>
                        <View style={styles.emptyCell} />
                    </View>
                    
                    <View style={styles.spacer} />
                    
                    {/* Organization Name and Title */}
                    <View style={styles.headerRowTwoCol}>
                        <Text style={[styles.headerCellLeft, styles.headerCellBold]}>{orgLabel1}</Text>
                        <Text style={[styles.headerCellRight, styles.title, { textAlign: 'left' }]}>OBJECT REPORT</Text>
                    </View>
                    <View style={styles.headerRowTwoCol}>
                        <Text style={[styles.headerCellLeft, styles.headerCellBold]}>{orgLabel2}</Text>
                        <Text style={styles.headerCellRight}><Text style={styles.headerCellLabel}>Contract Code:</Text> {contractCode || '-'}</Text>
                    </View>
                    <View style={styles.headerRowTwoCol}>
                        <Text style={[styles.headerCellLeft, styles.headerCellBold]}>{orgLabel3}</Text>
                        <Text style={[styles.headerCellRight, styles.headerCellBold]}>HELSINKI {formatFinnishDate()}</Text>
                    </View>
                    
                    <View style={styles.spacer} />
                    
                    {/* Contract and Location Info */}
                    <View style={styles.headerRowTwoCol}>
                        <Text style={styles.headerCellLeft}><Text style={styles.headerCellLabel}>Contract Name:</Text> {contractName || '-'}</Text>
                        <Text style={styles.headerCellRight}><Text style={styles.headerCellLabel}>Status:</Text> {statusName || '-'}</Text>
                    </View>
                    <View style={styles.headerRowTwoCol}>
                        <Text style={styles.headerCellLeft}><Text style={styles.headerCellLabel}>Location:</Text> {location || '-'}</Text>
                        <Text style={styles.headerCellRight}><Text style={styles.headerCellLabel}>Type:</Text> {typeName || '-'}</Text>
                    </View>
                    
                    <View style={styles.spacer} />
                    
                    {/* Planned Year and Order */}
                    <View style={styles.headerRowTwoCol}>
                        <Text style={styles.headerCellLeft}><Text style={styles.headerCellLabel}>Planned Year:</Text> {plannedYear || '-'}</Text>
                        <Text style={styles.headerCellRight}><Text style={styles.headerCellLabel}>Order Number:</Text> {orderNumber || '-'}</Text>
                    </View>
                    
                    {/* Network Number */}
                    <View style={styles.headerRowFullWidth}>
                        <Text><Text style={styles.headerRowFullWidthLabel}>Network Number:</Text> {networkNumber || '-'}</Text>
                    </View>
                </View>

                {/* Map Image - Shows the object on the map */}
                {mapImageUrl && (
                    <View style={styles.imageSection}>
                        <Text style={styles.imageTitle}>Object Location</Text>
                        <Image src={mapImageUrl} style={styles.image} />
                    </View>
                )}

                {/* Work Items by Group */}
                {Object.keys(groupedItems).length === 0 ? (
                    <View style={styles.table}>
                        <View style={styles.tableHeader}>
                            <Text style={styles.col1}>Type of Coating / Work Phase</Text>
                            <Text style={styles.col2}>Unit</Text>
                            <Text style={styles.col3}>Amount</Text>
                            <Text style={styles.col4}>Surface Area</Text>
                            <Text style={styles.col5}>Tons</Text>
                            <Text style={styles.col6}>Price €</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={{ width: '100%', textAlign: 'center', color: '#999' }}>
                                No planned work steps
                            </Text>
                        </View>
                    </View>
                ) : (
                    Object.entries(groupedItems).map(([groupName, groupItems]) => (
                        <View key={groupName} wrap={false}>
                            <Text style={styles.groupHeader}>{groupName}</Text>
                            
                            {/* Table Header */}
                            <View style={styles.tableHeader}>
                                <Text style={styles.col1}>Work Item</Text>
                                <Text style={styles.col2}>Unit</Text>
                                <Text style={styles.col3}>Amount</Text>
                                <Text style={styles.col4}>m²</Text>
                                <Text style={styles.col5}>Tons</Text>
                                <Text style={styles.col6}>Price €</Text>
                            </View>

                            {/* Table Rows */}
                            {groupItems.map((step, idx) => (
                                <View 
                                    key={step.id || idx} 
                                    style={idx % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
                                >
                                    <Text style={styles.col1}>{step.typeOfCoating || '-'}</Text>
                                    <Text style={styles.col2}>{step.unit || '-'}</Text>
                                    <Text style={styles.col3}>{formatNumber(step.amount)}</Text>
                                    <Text style={styles.col4}>{formatNumber(step.surfaceArea)}</Text>
                                    <Text style={styles.col5}>{formatNumber(step.tons)}</Text>
                                    <Text style={styles.col6}>{formatNumber(step.priceC)}</Text>
                                </View>
                            ))}
                        </View>
                    ))
                )}

                {/* Totals Section */}
                {Object.keys(groupedItems).length > 0 && (
                    <View style={styles.totalSection}>
                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>Total:</Text>
                            <Text style={[styles.totalValue, { color: '#2b8a3e' }]}>{formatCurrency(totalCost)}</Text>
                        </View>
                    </View>
                )}

                {/* Description Section */}
                {description && (
                    <View style={styles.descriptionSection}>
                        <Text style={styles.sectionTitle}>Description</Text>
                        <Text style={styles.descriptionText}>{description}</Text>
                    </View>
                )}

                {/* Footer */}
                <Text style={styles.footer}>
                    <Text style={{ fontWeight: 'bold' }}>ID:</Text><Text> {objectId || '-'}</Text>
                </Text>
            </Page>
        </Document>
    );
};
